import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faSearch } from '@fortawesome/free-solid-svg-icons';
import practiceService from '../../../services/practiceService';
import userRoleService from '../../../services/userRoleService';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import Table from '../../../components/Common/Table/Table';
import StatusBadge from '../../../components/Common/StatusBadge/StatusBadge';
import SkeletonLoader from '../../../components/SkeletonLoader/SkeletonLoader';
import ConfirmDeleteModal from '../../../components/Common/ConfirmDeleteModal/ConfirmDeleteModal';
import ReassignmentModal from '../../../components/Common/ReassignmentModal/ReassignmentModal';
import { ROLE_CHANGE_TARGETS, ROLES, getStaffRoleLabel } from '../../../utils/roleUtils';
import { matchesWordStartAny } from '../../../utils/searchMatch';
import {
  batchReassignPatientsToCaregiver,
  batchReassignPatientsToProvider,
  buildEmptyReassignments,
  buildReassignmentsForAll,
  fetchActiveCaregivers,
  fetchActiveProviders,
  fetchPatientsForStaff,
  getCaregiverAssigneeName,
  getProviderAssigneeName,
  hasUnassignedReassignments,
} from '../../../utils/staffReassignmentFlow';
import '../../PracticeManagement/GlobalDashboard/GlobalDashboard.css';
import './UserRoleManagement.css';

const LIST_ROLE_TABS = ROLE_CHANGE_TARGETS.filter((role) => role.group === 'Practice');

const staffNeedsPatientReassignment = (roleId) => {
  const id = Number(roleId);
  return id === ROLES.PROVIDER || id === ROLES.CAREGIVER;
};

const emptyReassignmentState = () => ({
  patients: [],
  alternatives: [],
  reassignments: {},
});

const UserRoleManagement = ({ embedded = false } = {}) => {
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const [practices, setPractices] = useState([]);
  const [selectedPracticeId, setSelectedPracticeId] = useState('');
  const [staff, setStaff] = useState([]);
  const [draftRoles, setDraftRoles] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [loadingPractices, setLoadingPractices] = useState(true);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeRoleTab, setActiveRoleTab] = useState(ROLES.PRACTICE_ADMIN);
  const [pendingRoleChange, setPendingRoleChange] = useState(null);
  const [showReassignmentModal, setShowReassignmentModal] = useState(false);
  const [reassignment, setReassignment] = useState(emptyReassignmentState);
  const [isCheckingPatients, setIsCheckingPatients] = useState(false);
  const [isReassigning, setIsReassigning] = useState(false);

  useEffect(() => {
    if (embedded) {
      updateBreadcrumbs([
        { label: 'Settings', path: '/settings' },
        { label: 'User Roles', path: '/settings?tab=user-roles', active: true },
      ]);
    } else {
      updateBreadcrumbs([
        { label: 'Settings', path: '/settings' },
        { label: 'User Roles', path: '/settings?tab=user-roles', active: true },
      ]);
    }
    return () => clearBreadcrumbs();
  }, [updateBreadcrumbs, clearBreadcrumbs, embedded]);

  useEffect(() => {
    const loadPractices = async () => {
      try {
        setLoadingPractices(true);
        const response = await practiceService.getPractices({ limit: 1000, status: 'Active' });
        const payload = response?.data;
        let list = [];
        if (payload?.success && payload?.data?.practices) {
          list = payload.data.practices;
        } else if (payload?.success && Array.isArray(payload?.data)) {
          list = payload.data;
        } else if (Array.isArray(payload)) {
          list = payload;
        }
        setPractices(list);
      } catch (err) {
        console.error('Failed to load practices:', err);
        toast.error('Failed to load practices');
      } finally {
        setLoadingPractices(false);
      }
    };
    loadPractices();
  }, []);

  const loadStaff = useCallback(async (practiceId) => {
    if (!practiceId) return;
    try {
      setLoadingStaff(true);
      const response = await userRoleService.getPracticeStaff(practiceId);
      const data = response?.data?.data;
      const rows = Array.isArray(data?.staff) ? data.staff : [];
      setStaff(rows);
      const drafts = {};
      rows.forEach((row) => {
        drafts[row.id] = Number(row.role_id);
      });
      setDraftRoles(drafts);
    } catch (err) {
      console.error('Failed to load staff:', err);
      toast.error(err?.response?.data?.message || 'Failed to load practice staff');
      setStaff([]);
      setDraftRoles({});
    } finally {
      setLoadingStaff(false);
    }
  }, []);

  const handlePracticeChange = (event) => {
    const practiceId = event.target.value;
    setSelectedPracticeId(practiceId);
    setSearchTerm('');
    setActiveRoleTab(ROLES.PRACTICE_ADMIN);
    if (practiceId) {
      loadStaff(practiceId);
    } else {
      setStaff([]);
      setDraftRoles({});
    }
  };

  const practiceStaff = useMemo(
    () => staff.filter((row) => LIST_ROLE_TABS.some((tab) => tab.id === Number(row.role_id))),
    [staff]
  );

  const filteredStaff = useMemo(() => {
    const forTab = practiceStaff.filter((row) => Number(row.role_id) === Number(activeRoleTab));
    if (!searchTerm.trim()) return forTab;
    return forTab.filter((row) =>
      matchesWordStartAny(
        [
          row.first_name,
          row.last_name,
          `${row.first_name || ''} ${row.last_name || ''}`,
          row.email,
          row.username,
        ],
        searchTerm
      )
    );
  }, [practiceStaff, activeRoleTab, searchTerm]);

  const roleTabCounts = useMemo(() => {
    const counts = {};
    LIST_ROLE_TABS.forEach((tab) => {
      counts[tab.id] = practiceStaff.filter((row) => Number(row.role_id) === tab.id).length;
    });
    return counts;
  }, [practiceStaff]);

  const handleDraftChange = (userId, roleId) => {
    setDraftRoles((prev) => ({ ...prev, [userId]: Number(roleId) }));
  };

  const handleSaveRole = (row) => {
    const nextRoleId = Number(draftRoles[row.id]);
    const currentRoleId = Number(row.role_id);
    if (!selectedPracticeId || !nextRoleId || nextRoleId === currentRoleId) return;

    const name = `${row.first_name || ''} ${row.last_name || ''}`.trim() || row.username || 'this user';
    setShowReassignmentModal(false);
    setReassignment(emptyReassignmentState());
    setPendingRoleChange({
      userId: row.id,
      name,
      currentRoleId,
      nextRoleId,
    });
  };

  const resetRoleChangeFlow = () => {
    setPendingRoleChange(null);
    setShowReassignmentModal(false);
    setReassignment(emptyReassignmentState());
    setIsCheckingPatients(false);
    setIsReassigning(false);
  };

  const cancelRoleChange = () => {
    if (savingId || isCheckingPatients || isReassigning) return;
    resetRoleChangeFlow();
  };

  const applyRoleChange = async (pending = pendingRoleChange) => {
    if (!pending || !selectedPracticeId) return false;
    const { userId, nextRoleId } = pending;
    try {
      setSavingId(userId);
      const response = await userRoleService.changeRole(selectedPracticeId, userId, nextRoleId);
      if (response?.data?.success) {
        toast.success(response.data.message || 'Role updated');
        resetRoleChangeFlow();
        if (LIST_ROLE_TABS.some((tab) => tab.id === Number(nextRoleId))) {
          setActiveRoleTab(Number(nextRoleId));
        }
        await loadStaff(selectedPracticeId);
        return true;
      }
      toast.error(response?.data?.message || 'Failed to update role');
      return false;
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update role');
      return false;
    } finally {
      setSavingId(null);
    }
  };

  const confirmRoleChange = async () => {
    if (!pendingRoleChange || !selectedPracticeId) return;
    const { userId, currentRoleId, name } = pendingRoleChange;
    const roleLabel = currentRoleId === ROLES.PROVIDER ? 'provider' : 'caregiver';

    if (!staffNeedsPatientReassignment(currentRoleId)) {
      await applyRoleChange();
      return;
    }

    setIsCheckingPatients(true);
    try {
      const patients = await fetchPatientsForStaff(
        selectedPracticeId,
        currentRoleId === ROLES.PROVIDER ? { providerId: userId } : { caregiverId: userId }
      );
      const alternatives =
        currentRoleId === ROLES.PROVIDER
          ? await fetchActiveProviders(selectedPracticeId, userId)
          : await fetchActiveCaregivers(selectedPracticeId, userId);

      if (!patients.length) {
        await applyRoleChange();
        return;
      }

      if (!alternatives.length) {
        toast.error(
          `Cannot change ${name}'s role until their patients are reassigned. Add another active ${roleLabel} first.`
        );
        return;
      }

      setReassignment({
        patients,
        alternatives,
        reassignments: buildEmptyReassignments(patients),
      });
      setShowReassignmentModal(true);
    } catch (err) {
      console.error('Failed to load patients for role change:', err);
      toast.error('Failed to check assigned patients.');
    } finally {
      setIsCheckingPatients(false);
    }
  };

  const handleReassignAndChangeRole = async () => {
    if (!pendingRoleChange || !selectedPracticeId) return;
    const { currentRoleId } = pendingRoleChange;
    const { patients, reassignments } = reassignment;

    if (hasUnassignedReassignments(patients, reassignments)) {
      toast.warning(
        currentRoleId === ROLES.PROVIDER
          ? 'Please select a new provider for all patients.'
          : 'Please select a new caregiver for all patients.'
      );
      return;
    }

    setIsReassigning(true);
    try {
      if (currentRoleId === ROLES.PROVIDER) {
        await batchReassignPatientsToProvider(selectedPracticeId, reassignments);
      } else {
        await batchReassignPatientsToCaregiver(selectedPracticeId, reassignments);
      }
      await applyRoleChange();
    } catch (err) {
      console.error('Reassignment before role change failed:', err);
      toast.error('Failed to reassign patients.');
    } finally {
      setIsReassigning(false);
    }
  };

  const isProviderReassignment = Number(pendingRoleChange?.currentRoleId) === ROLES.PROVIDER;
  const roleChangeBusy = Boolean(savingId) || isCheckingPatients;

  const columns = [
    {
      key: 'rowNum',
      title: '#',
      className: 'reusable-table-centered-col',
      render: (_row, rowIndex) => rowIndex + 1,
    },
    {
      key: 'name',
      title: 'Name',
      render: (row) => `${row.last_name || ''}, ${row.first_name || ''}`.replace(/^,\s*|,\s*$/g, '') || 'N/A',
    },
    {
      key: 'email',
      title: 'Email',
      render: (row) => row.email || 'N/A',
    },
    {
      key: 'username',
      title: 'Username',
      render: (row) => row.username || 'N/A',
    },
    {
      key: 'status',
      title: 'Status',
      className: 'reusable-table-centered-col',
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'current_role',
      title: 'Current Role',
      render: (row) => getStaffRoleLabel(row.role_id),
    },
    {
      key: 'new_role',
      title: 'Change To',
      render: (row) => {
        const draft = Number(draftRoles[row.id] ?? row.role_id);
        return (
          <select
            className="urm-role-select"
            value={draft}
            disabled={savingId === row.id}
            onChange={(e) => handleDraftChange(row.id, e.target.value)}
            aria-label={`New role for ${row.first_name || ''} ${row.last_name || ''}`}
          >
            {ROLE_CHANGE_TARGETS.map((role) => (
              <option key={role.id} value={role.id}>{role.label}</option>
            ))}
          </select>
        );
      },
    },
    {
      key: 'action',
      title: 'Action',
      className: 'reusable-table-centered-col',
      render: (row) => {
        const draft = Number(draftRoles[row.id] ?? row.role_id);
        const unchanged = draft === Number(row.role_id);
        const isSaving = savingId === row.id;
        return (
          <button
            type="button"
            className="urm-save-btn"
            disabled={unchanged || isSaving}
            onClick={() => handleSaveRole(row)}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        );
      },
    },
  ];

  return (
    <div className={`dashboard-container urm-page${embedded ? ' urm-page--embedded' : ''}`}>
      <div className="urm-toolbar">
        <div className="urm-practice-field">
          <label htmlFor="urm-practice-select">Practice</label>
          <select
            id="urm-practice-select"
            className="dashboard-select urm-practice-select"
            value={selectedPracticeId}
            onChange={handlePracticeChange}
            disabled={loadingPractices}
          >
            <option value="">
              {loadingPractices ? 'Loading practices…' : 'Select a practice'}
            </option>
            {practices.map((p) => (
              <option key={p.id} value={p.id}>
                {p.practice_name || p.name || `Practice ${p.id}`}
              </option>
            ))}
          </select>
        </div>

        {selectedPracticeId ? (
          <div className="search-container urm-search">
            <span className="search-label">Search:</span>
            <input
              type="text"
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Name, email, or role"
            />
            <button type="button" className="search-icon-btn" aria-label="Search">
              <FontAwesomeIcon icon={faSearch} />
            </button>
          </div>
        ) : null}
      </div>

      {!selectedPracticeId ? (
        <div className="urm-placeholder">
          <FontAwesomeIcon icon={faBuilding} className="urm-placeholder-icon" />
          <h4>Select a practice</h4>
          <p>Staff for that practice will appear here so you can change their roles.</p>
        </div>
      ) : loadingStaff ? (
        <SkeletonLoader type="mediaRows" count={4} />
      ) : (
        <>
          <div className="urm-role-tabs" role="tablist" aria-label="Staff type">
            {LIST_ROLE_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={Number(activeRoleTab) === tab.id}
                className={`urm-role-tab${Number(activeRoleTab) === tab.id ? ' is-active' : ''}`}
                onClick={() => {
                  setActiveRoleTab(tab.id);
                  setSearchTerm('');
                }}
              >
                {tab.label}
                <span className="urm-role-tab-count">{roleTabCounts[tab.id] || 0}</span>
              </button>
            ))}
          </div>
          {practiceStaff.length === 0 ? (
            <div className="urm-placeholder">
              <h4>No staff found</h4>
              <p>This practice has no caregivers, providers, or practice admins to change.</p>
            </div>
          ) : (
            <div className="urm-table-wrap">
              <Table
                columns={columns}
                data={filteredStaff}
                isLoading={false}
                getRowKey={(row) => row.id}
                emptyMessage={
                  searchTerm.trim()
                    ? `No ${getStaffRoleLabel(activeRoleTab).toLowerCase()}s match this search.`
                    : `No ${getStaffRoleLabel(activeRoleTab).toLowerCase()}s in this practice.`
                }
              />
            </div>
          )}
        </>
      )}

      <ConfirmDeleteModal
        show={Boolean(pendingRoleChange) && !showReassignmentModal}
        className="urm-role-confirm-modal"
        title="Change Role"
        message={
          pendingRoleChange ? (
            <>
              <p>
                Change <strong>{pendingRoleChange.name}</strong> from{' '}
                <strong>{getStaffRoleLabel(pendingRoleChange.currentRoleId)}</strong> to{' '}
                <strong>{getStaffRoleLabel(pendingRoleChange.nextRoleId)}</strong>?
              </p>
              {staffNeedsPatientReassignment(pendingRoleChange.currentRoleId) ? (
                <p>If they have assigned patients, you will be asked to reassign those patients first.</p>
              ) : (
                <p>They will need to sign in again for the new permissions to apply.</p>
              )}
            </>
          ) : null
        }
        warning=""
        confirmLabel="Change Role"
        busyLabel={isCheckingPatients ? 'Checking patients...' : 'Saving...'}
        isDeleting={roleChangeBusy}
        onConfirm={confirmRoleChange}
        onCancel={cancelRoleChange}
      />

      <ReassignmentModal
        show={showReassignmentModal}
        onClose={cancelRoleChange}
        title="Reassign Patients"
        description={
          pendingRoleChange
            ? `Select a new ${isProviderReassignment ? 'provider' : 'caregiver'} for each of the ${reassignment.patients.length} patient(s) before changing ${pendingRoleChange.name}'s role to ${getStaffRoleLabel(pendingRoleChange.nextRoleId)}.`
            : ''
        }
        patients={reassignment.patients}
        alternatives={reassignment.alternatives}
        reassignments={reassignment.reassignments}
        onReassignmentChange={(patientId, assigneeId) =>
          setReassignment((prev) => ({
            ...prev,
            reassignments: { ...prev.reassignments, [patientId]: assigneeId },
          }))
        }
        onAssignAll={(assigneeId) =>
          setReassignment((prev) => ({
            ...prev,
            reassignments: buildReassignmentsForAll(prev.patients, assigneeId),
          }))
        }
        onSave={handleReassignAndChangeRole}
        isSaving={isReassigning || Boolean(savingId)}
        saveLabel="Reassign & Change Role"
        optionPlaceholder={isProviderReassignment ? 'Select provider...' : 'Select caregiver...'}
        currentLabel={isProviderReassignment ? 'Current provider' : 'Current caregiver'}
        reassignToLabel={isProviderReassignment ? 'Reassign to provider' : 'Reassign to caregiver'}
        getCurrentAssignee={isProviderReassignment ? getProviderAssigneeName : getCaregiverAssigneeName}
      />
    </div>
  );
};

export default UserRoleManagement;
